'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { PdfThumbnail } from './PdfThumbnail';

export interface ResourceCard {
  category: string
  title: string
  dek?: string
  href: string
  imageSrc: string
  authorName?: string
  date?: string
  readMinutes?: number
  /** @deprecated kept for backward compat — no longer rendered */
  readTime?: string
  pdfUrl?: string
  pdfPageCount?: number
}

interface SuggestedResourcesProps {
  heading?: string
  cards: ResourceCard[]
  viewAllHref?: string
  viewAllLabel?: string
}

// Module-level cache shared with resources listing page
const _pageCountCache = new Map<string, number>();

function usePdfPageCount(pdfUrl: string | undefined, initial: number | undefined): number | null {
  const [count, setCount] = useState<number | null>(() => {
    if (initial) return initial;
    if (pdfUrl && _pageCountCache.has(pdfUrl)) return _pageCountCache.get(pdfUrl)!;
    return null;
  });

  useEffect(() => {
    if (!pdfUrl || count !== null) return;
    if (_pageCountCache.has(pdfUrl)) { setCount(_pageCountCache.get(pdfUrl)!); return; }
    let cancelled = false;
    fetch(`/api/pdf-page-count?url=${encodeURIComponent(pdfUrl)}`)
      .then(r => r.ok ? r.json() : null)
      .then((data: { pages: number } | null) => {
        if (!cancelled && data?.pages) {
          _pageCountCache.set(pdfUrl, data.pages);
          setCount(data.pages);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pdfUrl, count]);

  return count;
}

function displayCategory(cat: string | undefined): string {
  if (!cat) return '';
  if (cat === 'White Paper') return 'Whitepaper';
  if (cat === 'White Papers') return 'Whitepapers';
  return cat;
}

function MetaSep() {
  return <span className="bl-sep" aria-hidden="true" />;
}

function SuggestedCard({ card }: { card: ResourceCard }) {
  const pages = usePdfPageCount(card.pdfUrl, card.pdfPageCount);
  const isResource = !!card.pdfUrl;

  return (
    <Link href={card.href} className="bl-post-card">
      {/* bl-post-card__media needs the div-fill rule; add it inline so it works
          regardless of whether an rl-page ancestor exists */}
      <div className="bl-post-card__media" style={{ backgroundImage: `url(${card.imageSrc || '/Whitepaper%20thumbnail%20bg.png'})` }}>
        <PdfThumbnail pdfUrl={card.pdfUrl} heroImg={card.imageSrc} />
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{card.title}</h3>
        <div className="bl-post-card__meta">
          {isResource ? (
            // Resource card: category · date · N pages
            <>
              {card.category && (
                <><span className="bl-post-card__meta-text">{displayCategory(card.category)}</span><MetaSep /></>
              )}
              {card.date && <span className="bl-post-card__meta-text">{card.date}</span>}
              {pages ? <><MetaSep /><span className="bl-post-card__meta-text">{pages} pages</span></> : null}
            </>
          ) : (
            // Blog card: category · date · read time
            <>
              {card.category && (
                <><span className="bl-post-card__meta-text">{card.category}</span><MetaSep /></>
              )}
              {card.date && <span className="bl-post-card__meta-text">{card.date}</span>}
              {card.readMinutes ? <><MetaSep /><span className="bl-post-card__meta-text">{card.readMinutes} min read</span></> : null}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export function SuggestedResources({
  heading = 'Keep reading',
  cards,
  viewAllHref = '/blogs',
  viewAllLabel = 'View all',
}: SuggestedResourcesProps) {
  if (cards.length === 0) return null;
  return (
    <section
      className="section-x-pad"
      style={{
        background: 'var(--seasons-color-surface-default)',
        paddingTop: 'var(--seasons-space-20)',
        paddingBottom: 'var(--seasons-space-20)',
        borderTop: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
      }}
    >
      <div
        className="suggested-resources-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--seasons-space-10)',
        }}
      >
        <h2
          className="type-display-md"
          style={{ color: 'var(--seasons-color-prose-heading)', margin: 0 }}
        >
          {heading}
        </h2>
        <Link href={viewAllHref} className="block-button-link suggested-resources-viewall">
          <div className="block-button block-button--sm block-button--outline">
            <div className="block-button__text">
              <span className="btn-primary-text">{viewAllLabel}</span>
              <span className="btn-hover-text">{viewAllLabel}</span>
            </div>
          </div>
        </Link>
      </div>
      <div
        className="suggested-resources-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--seasons-space-6)',
        }}
      >
        {cards.map((card, i) => (
          <SuggestedCard key={`${card.href}-${i}`} card={card} />
        ))}
      </div>

      {/* Mobile-only: view all after cards */}
      <Link href={viewAllHref} className="block-button-link suggested-resources-viewall--mobile" style={{ marginTop: 'var(--seasons-space-8)', display: 'none' }}>
        <div className="block-button block-button--md block-button--outline" style={{ width: '100%', justifyContent: 'center' }}>
          <div className="block-button__text">
            <span className="btn-primary-text">{viewAllLabel}</span>
            <span className="btn-hover-text">{viewAllLabel}</span>
          </div>
        </div>
      </Link>
    </section>
  );
}
